import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Send, TrendingUp, Sparkles, ShieldAlert, Wand2, Loader2, Palette, BookOpen, ExternalLink, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { API, formatErr } from "@/lib/api";
import useSonioxLive from "@/hooks/useSonioxLive";

const HIGHLIGHT_COLORS = {
  cancel:  "bg-red-100 dark:bg-red-500/25 text-red-800 dark:text-red-100",
  refund:  "bg-emerald-100 dark:bg-emerald-500/25 text-emerald-800 dark:text-emerald-100",
  price:   "bg-amber-100 dark:bg-amber-500/25 text-amber-800 dark:text-amber-100",
  default: "bg-accent/15 text-foreground",
};

function highlightCls(word) {
  const k = (word || "").toLowerCase().replace(/[^a-z]/g, "");
  return HIGHLIGHT_COLORS[k] || HIGHLIGHT_COLORS.default;
}

// Stream a suggested reply over Server-Sent Events (fetch + ReadableStream so we
// can POST a body AND send the Bearer token — native EventSource can't do either).
async function streamCompose(body, { onMeta, onDelta, onDone, onError, signal } = {}) {
  const token = localStorage.getItem("c7_token");
  const res = await fetch(`${API}/ai/copilot/compose/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`compose stream failed: ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  const dispatch = (raw) => {
    let event = "message";
    let data = "";
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
    }
    if (!data) return;
    let parsed;
    try { parsed = JSON.parse(data); } catch { return; }
    if (event === "meta") onMeta?.(parsed);
    else if (event === "delta") onDelta?.(parsed.text || "");
    else if (event === "done") onDone?.(parsed);
    else if (event === "error") onError?.(parsed);
  };
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (raw.trim()) dispatch(raw);
    }
  }
  if (buf.trim()) dispatch(buf);
}

export default function AiCopilot({ conversation }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [llmSuggestion, setLlmSuggestion] = useState(null); // {suggestion, model, tone_label, is_mocked}
  const [llmSentiment, setLlmSentiment] = useState(null);   // {label, score, summary, model}
  const [llmBusy, setLlmBusy] = useState(false);
  const [tones, setTones] = useState([]);                   // [{id,label,description,overlay}]
  const [tone, setTone] = useState(
    () => localStorage.getItem("connect7.copilot.tone") || "warm-professional"
  );
  const [useKB, setUseKB] = useState(
    () => localStorage.getItem("connect7.copilot.useKB") !== "0"
  );
  const [autoRegen, setAutoRegen] = useState(true);   // admin: regen on channel switch

  // ---- Live-call detection + Soniox real-time transcription ----------------
  const isLive = conversation?.live_call === true ||
    (typeof conversation?.id === "string" && conversation.id.startsWith("live-"));
  // The channel/thread the agent is currently VIEWING (emitted by
  // ConversationView) — the copilot must follow this, not the primary channel.
  const [ctx, setCtx] = useState(null); // {conversation_id, channel, messages, read_only}
  useEffect(() => {
    const onCtx = (e) => setCtx(e?.detail || null);
    window.addEventListener("copilot:context", onCtx);
    return () => window.removeEventListener("copilot:context", onCtx);
  }, []);
  useEffect(() => { setCtx(null); }, [conversation?.id]);
  const chatChannel = ctx?.channel || conversation?.primary_channel;
  const activeConvId = ctx?.conversation_id || conversation?.id;
  const activeMessages = (ctx && ctx.messages) ? ctx.messages : (conversation?.messages || []);
  // A real digital conversation (Instagram/WhatsApp/etc.) opened from the inbox.
  const isDigitalChat = !!(conversation?.id && chatChannel && chatChannel !== "voice" && !isLive);
  const [liveStream, setLiveStream] = useState(null);
  const [sonioxAvailable, setSonioxAvailable] = useState(false);
  const composeBusyRef = useRef(false);
  const lastComposedRef = useRef("");
  const pendingComposeRef = useRef(null);   // latest transcript queued while busy
  const streamAbortRef = useRef(null);      // aborts an in-flight compose stream
  const liveTurnsRef = useRef([]);          // live-call turns incl. copilot's own replies

  // Is live Soniox transcription configured on this tenant?
  useEffect(() => {
    let cancelled = false;
    api.get("/ai/soniox/status")
      .then((r) => { if (!cancelled) setSonioxAvailable(!!r.data?.enabled); })
      .catch(() => { /* leave disabled */ });
    return () => { cancelled = true; };
  }, []);

  // While a call is live, obtain the customer (remote) audio MediaStream. We
  // prefer the EXACT stream the softphone is playing (window.__c7RemoteAudioStream,
  // published by useSipEngine) and fall back to the hidden <audio> element.
  // Re-acquire whenever the stream changes (connect / hold / unhold).
  useEffect(() => {
    if (!isLive) { setLiveStream(null); return; }
    let cancelled = false;
    let lastSrc = null;
    let ticks = 0;
    let timer = null;
    const pickFromElement = () => {
      const el = document.querySelector('[data-testid="sip-remote-audio"]');
      const src = el?.srcObject || null;
      if (src && src.getAudioTracks && src.getAudioTracks().length > 0) {
        try {
          const cap = (typeof el.captureStream === "function" && el.captureStream())
            || (typeof el.mozCaptureStream === "function" && el.mozCaptureStream());
          if (cap && cap.getAudioTracks && cap.getAudioTracks().length > 0) return cap;
        } catch { /* keep raw */ }
        return src;
      }
      return null;
    };
    const check = () => {
      if (cancelled) return;
      // 1) exact stream from the SIP engine (most reliable)
      const g = (typeof window !== "undefined" && window.__c7RemoteAudioStream) || null;
      const gOk = !!(g && g.getAudioTracks && g.getAudioTracks().length > 0);
      const chosen = gOk ? g : pickFromElement();
      if (chosen && chosen !== lastSrc) {
        lastSrc = chosen;
        setLiveStream(chosen);
      } else if (!chosen && lastSrc) {
        lastSrc = null;
        setLiveStream(null);
      }
      if (ticks++ % 5 === 0) {
        const el = document.querySelector('[data-testid="sip-remote-audio"]');
        // eslint-disable-next-line no-console
        console.log("[c7][copilot-audio]",
          "globalStream:", gOk ? `${g.getAudioTracks().length} track(s)` : "none",
          "| element:", el ? "found" : "missing",
          "| element.srcObject tracks:", el?.srcObject?.getAudioTracks?.().length ?? "n/a",
          "| liveStreamSet:", !!chosen);
      }
    };
    const loop = () => {
      check();
      if (!cancelled) timer = setTimeout(loop, 1000);
    };
    loop();
    const onBind = () => check();   // re-check immediately; single timer chain
    window.addEventListener("c7:remote-audio", onBind);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("c7:remote-audio", onBind);
    };
  }, [isLive, conversation?.id]);

  // Stream a suggested reply (SSE) so it types out live; the sentiment call runs
  // decoupled so the reply appears the instant it starts (not blocked on it).
  async function generateReply(composeBody, sentimentText, opts = {}) {
    const { notify = false } = opts;
    if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch { /* ignore */ } }
    const ac = new AbortController();
    streamAbortRef.current = ac;
    setLlmBusy(true);
    if (sentimentText) {
      api.post("/ai/sentiment", { text: sentimentText.slice(0, 1000) })
        .then((r) => setLlmSentiment(r.data))
        .catch(() => { /* sentiment is best-effort */ });
    }
    setLlmSuggestion({ suggestion: "", streaming: true });
    let acc = "";
    let finalDone = null;
    try {
      await streamCompose(composeBody, {
        signal: ac.signal,
        onMeta: (m) => setLlmSuggestion((p) => ({ ...(p || {}), ...m, suggestion: acc, streaming: true })),
        onDelta: (t) => { acc += t; setLlmSuggestion((p) => ({ ...(p || {}), suggestion: acc, streaming: true })); },
        onDone: (d) => {
          finalDone = d;
          setLlmSuggestion({ ...d, streaming: false });
          if (notify) {
            const cit = (d?.citations || []).length;
            toast.success(`Refreshed · ${d?.tone_label || d?.tone || "LLM"}${cit ? ` · ${cit} KB cite${cit > 1 ? "s" : ""}` : ""}`);
          }
        },
      });
      return finalDone;
    } catch (e) {
      if (e?.name === "AbortError") return null;
      // Streaming failed — fall back to the blocking endpoint so the agent
      // still gets a reply.
      try {
        const r = await api.post("/ai/copilot/compose", composeBody);
        setLlmSuggestion(r.data);
        if (notify) toast.success(`Refreshed · ${r.data?.tone_label || "LLM"}`);
        return r.data;
      } catch (e2) { toast.error(formatErr(e2)); return null; }
    } finally {
      if (streamAbortRef.current === ac) streamAbortRef.current = null;
      setLlmBusy(false);
    }
  }

  async function composeFromLive(fullTranscript, latestUtterance) {
    // Answer the customer's CURRENT utterance; earlier turns give context.
    const current = (latestUtterance || "").trim() || (fullTranscript || "").trim();
    if (!current) return;
    // If a compose is already running, remember the newest utterance and run it
    // right after — never silently drop a question.
    if (composeBusyRef.current) { pendingComposeRef.current = { fullTranscript, latestUtterance }; return; }
    if (current === lastComposedRef.current) return; // this question already answered
    lastComposedRef.current = current;
    composeBusyRef.current = true;
    // Record the customer's turn so the copilot keeps full conversational memory
    // (incl. its OWN prior replies) — this is what lets it read a bare "No" as a
    // reply to its previous question instead of a fresh negative.
    const turns = liveTurnsRef.current;
    if (!turns.length || turns[turns.length - 1].text !== current) {
      turns.push({ role: "customer", text: current });
    }
    try {
      const history = turns.slice(-9, -1).map((t) => ({ role: t.role, text: t.text }));
      const res = await generateReply(
        { history, current_message: current, tone, use_kb: useKB },
        current,
      );
      if (res?.suggestion) liveTurnsRef.current.push({ role: "agent", text: res.suggestion });
    } catch { /* transient — next utterance retries */ }
    finally {
      composeBusyRef.current = false;
      const queued = pendingComposeRef.current;
      pendingComposeRef.current = null;
      if (queued) {
        const q = (queued.latestUtterance || "").trim() || (queued.fullTranscript || "").trim();
        if (q && q !== lastComposedRef.current) {
          composeFromLive(queued.fullTranscript, queued.latestUtterance);
        }
      }
    }
  }

  const sonioxEnabled = isLive && sonioxAvailable && !!liveStream;
  const { finalText: liveFinal, interim: liveInterim, status: liveStatus, reset: resetLive } =
    useSonioxLive(liveStream, sonioxEnabled, composeFromLive);

  // Reset accumulated live transcript when the call (conversation) changes.
  useEffect(() => {
    resetLive();
    lastComposedRef.current = "";
    pendingComposeRef.current = null;
    liveTurnsRef.current = [];
    if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch { /* ignore */ } streamAbortRef.current = null; }
  }, [conversation?.id, resetLive]);

  // Fetch tone presets once (cached in /ai/config payload).
  useEffect(() => {
    let cancelled = false;
    api.get("/ai/config")
      .then((r) => {
        if (cancelled) return;
        setTones(r.data?.tones || []);
        setAutoRegen(r.data?.copilot_auto_regenerate !== false);
      })
      .catch(() => { /* silent — fallback to default tone string */ });
    return () => { cancelled = true; };
  }, []);

  function pickTone(v) {
    setTone(v);
    localStorage.setItem("connect7.copilot.tone", v);
    // If we already have a suggestion, regenerate so the agent sees the new style.
    if (llmSuggestion && conversation?.id) regenerateWithLLM(v);
  }

  function toggleKB(v) {
    setUseKB(!!v);
    localStorage.setItem("connect7.copilot.useKB", v ? "1" : "0");
    if (llmSuggestion && conversation?.id) regenerateWithLLM(tone, !!v);
  }

  useEffect(() => {
    if (!conversation?.id) { setData(null); setLlmSuggestion(null); setLlmSentiment(null); return; }
    // Synthetic "live call" conversations (id prefix `live-`) have no
    // backing transcript / copilot record yet — skip the fetch and let
    // the agent press "Use real LLM" once they have something to ask.
    if (typeof conversation.id === "string" && conversation.id.startsWith("live-")) {
      setData({
        lead_score: null,
        lead_label: "Live call",
        expected_value: "—",
        sentiment: "neutral",
        transcription: [],
        suggested_reply: "Listening to the customer — a suggested reply will appear automatically. You can also press Use real LLM anytime.",
      });
      setLlmSuggestion(null); setLlmSentiment(null);
      return;
    }
    setLoading(true);
    setLlmSuggestion(null); setLlmSentiment(null);
    api.get(`/copilot/${conversation.id}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [conversation?.id]);

  // For a digital chat, auto-generate a context-aware reply from the REAL
  // conversation. Always runs when a NEW conversation opens; on a channel
  // switch WITHIN the same conversation it re-runs only when the admin has
  // enabled `copilot_auto_regenerate` (else the agent clicks Regenerate).
  const genKeyRef = useRef(null);
  useEffect(() => {
    if (!(isDigitalChat && conversation?.id)) return;
    const convKey = String(conversation.id);
    const fullKey = `${convKey}::${chatChannel}::${activeConvId}`;
    const prev = genKeyRef.current;
    const convChanged = !prev || !prev.startsWith(convKey + "::");
    if (convChanged) {
      genKeyRef.current = fullKey;
      regenerateWithLLM();
    } else if (fullKey !== prev) {
      genKeyRef.current = fullKey;
      if (autoRegen) regenerateWithLLM();
    }
    // eslint-disable-next-line
  }, [conversation?.id, isDigitalChat, chatChannel, activeConvId, autoRegen]);

  // The canned `/copilot/{id}` suggestion is ONLY valid for the simulated voice
  // copilot record — for a real digital chat or a live call we show the LLM
  // suggestion alone (never the static fallback line).
  const showCanned = !isDigitalChat && !isLive;
  const suggestionText = llmSuggestion?.suggestion || (showCanned ? data?.suggested_reply : "");

  // Send the suggested reply: for a digital chat it goes OUT on that channel
  // (Instagram/etc.); otherwise it's copied for the agent to use on the call.
  async function sendReply() {
    const text = suggestionText;
    if (!text) return;
    // Read-only history channels (e.g. Email) need the composer (subject etc.),
    // so insert the reply into the composer rather than posting blind.
    const canSendInline = isDigitalChat && activeConvId && chatChannel !== "email" && !ctx?.read_only;
    if (canSendInline) {
      try {
        await api.post(`/conversations/${activeConvId}/messages`, { body: text, channel: chatChannel });
        toast.success(`Sent via ${chatChannel}`);
        window.dispatchEvent(new CustomEvent("copilot:sent", { detail: { conversation_id: activeConvId } }));
      } catch (e) { toast.error(formatErr(e)); }
    } else if (isDigitalChat) {
      // Email / read-only digital → drop into the composer (needs subject/edit).
      insertReply();
    } else {
      // Pure voice / live call — no composer; copy for the agent to paste.
      try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
      toast.success("Reply copied — paste it into the conversation");
    }
  }

  // Insert the suggested reply into the channel composer (or email body) so the
  // agent can edit before sending — the single, seamless path across channels.
  function insertReply() {
    const text = suggestionText;
    if (!text) return;
    window.dispatchEvent(new CustomEvent("copilot:insert", {
      detail: { channel: chatChannel, text },
    }));
    toast.success(chatChannel === "email"
      ? "Inserted into the email reply — add a subject & edit"
      : `Inserted into the ${chatChannel} reply — edit & send`);
  }

  async function regenerateWithLLM(toneOverride, kbOverride) {
    if (!conversation?.id) return;
    const isSynthetic = typeof conversation.id === "string" && conversation.id.startsWith("live-");
    // Prefer the messages actually SHOWN on the selected channel (so the
    // copilot follows the channel switch); fall back to the conversation doc.
    const msgs = activeMessages || [];
    const lastCustomer = msgs.slice().reverse().find((m) => (m.from || "customer") === "customer");
    const sentimentText = lastCustomer?.body || conversation?.preview || "";
    const kb = kbOverride === undefined ? useKB : !!kbOverride;
    let composeBody;
    if (isSynthetic) {
      composeBody = {
        history: [{
          role: "customer",
          text: "Customer has just connected on a live voice call — generate an opening line the agent can use.",
        }],
        tone: toneOverride || tone, use_kb: kb,
      };
    } else if (msgs.length > 0) {
      // Channel-specific: send the visible thread so the reply matches the
      // channel the agent is currently looking at.
      composeBody = {
        history: msgs.slice(-12).map((m) => ({
          role: (m.from || "customer") === "agent" ? "agent" : "customer",
          text: m.body || "",
        })),
        tone: toneOverride || tone, use_kb: kb,
      };
    } else {
      composeBody = {
        conversation_id: activeConvId,
        tone: toneOverride || tone, use_kb: kb,
      };
    }
    await generateReply(composeBody, sentimentText, { notify: true });
  }

  // Group transcription chunks into "lines" by speaker so labels appear once per line
  const lines = useMemo(() => {
    const out = [];
    let cur = null;
    (data?.transcription || []).forEach((t) => {
      if (!cur || cur.speaker !== t.speaker) {
        cur = { speaker: t.speaker, parts: [] };
        out.push(cur);
      }
      cur.parts.push(t);
    });
    return out;
  }, [data]);

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="px-5 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-accent" />
          <h3 className="font-display font-bold tracking-tight">AI Copilot</h3>
        </div>
      </div>

      <ScrollArea className="flex-1 px-5 mt-4 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
        {!conversation && (
          <div className="text-center text-sm text-muted-foreground py-12">
            <Sparkles className="w-6 h-6 mx-auto mb-2 opacity-60" />
            Pick a conversation to see live insights.
          </div>
        )}

        {conversation && (
          <div className="space-y-4 pb-6">
            {/* Conversion Intel */}
            <div className="relative rounded-xl p-5 bg-secondary text-secondary-foreground overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/40 blur-3xl pointer-events-none" />
              <div className="flex items-center justify-between relative">
                <span className="text-overline opacity-90">Conversion Intel</span>
                <div className="flex items-center gap-1.5">
                  {!loading && data && (
                    <Badge
                      variant="outline"
                      data-testid="copilot-intel-source"
                      className={`text-[9px] uppercase tracking-wider border-0 ${
                        data?.insufficient
                          ? "bg-white/15 text-white/80"
                          : data?.is_mocked
                          ? "bg-amber-400/25 text-amber-100"
                          : "bg-emerald-400/25 text-emerald-100"
                      }`}
                      title={data?.rationale || ""}
                    >
                      {data?.insufficient ? "no data" : data?.is_mocked ? "estimate" : "AI live"}
                    </Badge>
                  )}
                  <TrendingUp className="w-4 h-4 opacity-90" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6 relative">
                <div>
                  <div className="text-overline opacity-80">Lead Score</div>
                  <div className="font-display font-extrabold text-3xl mt-1 text-emerald-300" data-testid="copilot-lead-score">
                    {loading || data?.lead_score == null ? "—" : `${data.lead_score}%`} <span className="text-xs font-medium opacity-80">({data?.lead_label || "—"})</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-overline opacity-80">Expected Value</div>
                  <div className="font-display font-extrabold text-3xl mt-1" data-testid="copilot-expected-value">{data?.expected_value || "—"}</div>
                </div>
              </div>
              {!loading && data?.rationale && (
                <div className="mt-4 text-[11px] opacity-80 leading-snug relative" data-testid="copilot-intel-rationale">
                  {data.rationale}
                </div>
              )}
            </div>

            {/* Live transcription (voice) / Conversation context (chat) */}
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                {isDigitalChat ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5 text-accent" />
                    <span className="text-sm font-medium">Conversation</span>
                    <Badge variant="outline" data-testid="copilot-conversation-badge" className="ml-auto text-[9px] uppercase tracking-wider">{chatChannel}</Badge>
                  </>
                ) : (
                  <>
                    <span className="relative inline-block w-2 h-2 rounded-full bg-red-500 text-red-500 live-pulse" />
                    <span className="text-[11px] font-bold tracking-wider text-red-600 dark:text-red-400">LIVE</span>
                    <span className="text-sm font-medium">Transcription</span>
                    {isLive && (
                      <Badge variant="outline" className="ml-auto text-[9px] uppercase tracking-wider" data-testid="copilot-soniox-status">
                        {!sonioxAvailable ? "STT off" :
                          liveStatus === "live" ? "Soniox · listening" :
                          liveStatus === "no-audio" ? "No audio" :
                          liveStatus === "connecting" ? "Connecting…" :
                          liveStatus === "error" ? "STT error" : "Waiting for audio"}
                      </Badge>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-3 text-sm leading-relaxed" data-testid="copilot-transcription">
                {isDigitalChat ? (
                  (activeMessages || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No messages yet.</div>
                  ) : (
                    (activeMessages || []).slice(-6).map((m, i) => {
                      const agent = (m.from || "customer") === "agent";
                      return (
                        <div key={m.id || i} className={`pl-3 border-l-2 ${agent ? "border-accent" : "border-fuchsia-500"}`}>
                          <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1 ${agent ? "text-accent" : "text-fuchsia-600 dark:text-fuchsia-400"}`}>
                            {agent ? "Agent" : "Customer"}
                          </div>
                          <div className="text-foreground/90 break-words">{m.body}</div>
                        </div>
                      );
                    })
                  )
                ) : isLive ? (
                  (liveFinal || liveInterim) ? (
                    <div className="pl-3 border-l-2 border-fuchsia-500">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1 text-fuchsia-600 dark:text-fuchsia-400">Customer</div>
                      <div className="text-foreground/90">
                        {liveFinal}
                        {liveInterim && <span className="opacity-50"> {liveInterim}</span>}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs italic" data-testid="copilot-transcription-status">
                      {!sonioxAvailable ? (
                        <span className="text-muted-foreground">Live transcription not configured.</span>
                      ) : liveStatus === "no-audio" ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          No audio detected on the customer's line. Check the call is connected and not on hold — transcription resumes automatically once audio returns.
                        </span>
                      ) : liveStatus === "error" ? (
                        <span className="text-red-600 dark:text-red-400">
                          Transcription error. Retrying automatically… (check the STT key in AI Suite if this persists).
                        </span>
                      ) : liveStatus === "connecting" ? (
                        <span className="text-muted-foreground">Connecting to live transcription…</span>
                      ) : (
                        <span className="text-muted-foreground">Waiting for the customer to speak…</span>
                      )}
                    </div>
                  )
                ) : (
                  <>
                    {lines.length === 0 && <div className="text-xs text-muted-foreground italic">Waiting for transcription…</div>}
                    {lines.map((line, i) => {
                      const isAgent = line.speaker === "agent";
                      return (
                        <div key={i} className={`pl-3 border-l-2 ${isAgent ? "border-accent" : "border-fuchsia-500"}`}>
                          <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1 ${isAgent ? "text-accent" : "text-fuchsia-600 dark:text-fuchsia-400"}`}>
                            {line.speaker === "customer" ? "Customer" : "Agent"}
                          </div>
                          <div className="text-foreground/90">
                            {line.parts.map((p, j) => (
                              <span key={j}>
                                {p.highlights?.length ? (
                                  <span className={`inline-block px-1 rounded font-semibold ${highlightCls(p.text)}`}>{p.text}</span>
                                ) : (
                                  p.text
                                )}{" "}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>

            {/* Suggested reply */}
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">Suggested Reply</div>
                <div className="flex items-center gap-2">
                  {llmSentiment ? (
                    <Badge className={`border-0 shrink-0 ${
                      llmSentiment.label === "negative" || llmSentiment.label === "frustrated"
                        ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                        : llmSentiment.label === "positive" || llmSentiment.label === "happy"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                        : "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300"
                    }`}>
                      {llmSentiment.label}
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300 border-0 shrink-0">{data?.sentiment || "—"}</Badge>
                  )}
                  <Button
                    data-testid="copilot-regenerate"
                    size="sm"
                    variant="outline"
                    onClick={() => regenerateWithLLM()}
                    disabled={llmBusy || !conversation?.id}
                    className="h-7 px-2 gap-1.5 text-xs"
                  >
                    {llmBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Wand2 className="w-3 h-3"/>}
                    {llmSuggestion ? "Regenerate" : "Use real LLM"}
                  </Button>
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground/90 leading-relaxed italic break-words" data-testid="copilot-suggestion">
                {llmSuggestion?.suggestion
                  ? <>{`"${llmSuggestion.suggestion}`}{llmSuggestion?.streaming && <span className="not-italic animate-pulse">▍</span>}{`"`}</>
                  : (!showCanned && llmBusy)
                    ? "Generating a contextual reply…"
                    : suggestionText
                      ? `"${suggestionText}"`
                      : "—"}
              </p>
              {(llmSuggestion?.model || llmSentiment?.model || llmSuggestion?.is_mocked) && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {llmSuggestion?.tone_label && (
                    <Badge variant="outline" className="text-[10px] bg-secondary/40">{llmSuggestion.tone_label}</Badge>
                  )}
                  {llmSuggestion?.model && (
                    <Badge variant="outline" className="text-[10px]">{llmSuggestion.model}</Badge>
                  )}
                  {llmSuggestion?.is_mocked && (
                    <Badge
                      variant="outline"
                      className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300"
                      title={llmSuggestion?.error_detail || (llmSuggestion?.error ? `LLM unavailable (${llmSuggestion.error}) — check EMERGENT_LLM_KEY / backend logs` : "LLM unavailable — using fallback")}
                      data-testid="copilot-fallback-badge"
                    >
                      fallback{llmSuggestion?.error ? ` · ${llmSuggestion.error}` : ""}
                    </Badge>
                  )}
                </div>
              )}
              {tones.length > 0 && (
                <div className="mt-3 flex items-center gap-2 min-w-0">
                  <Palette className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground shrink-0">Voice</span>
                  <Select value={tone} onValueChange={pickTone} disabled={llmBusy}>
                    <SelectTrigger
                      data-testid="copilot-tone"
                      className="h-7 text-xs flex-1 min-w-0 max-w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tones.map((t) => (
                        <SelectItem key={t.id} value={t.id} title={t.description}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* KB toggle */}
              <div className="mt-2 flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0"/>
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground shrink-0">Use KB</span>
                <Switch
                  data-testid="copilot-use-kb"
                  checked={useKB}
                  onCheckedChange={toggleKB}
                  disabled={llmBusy}
                  className="scale-75 origin-left"
                />
                {llmSuggestion?.kb_used && (
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 dark:bg-emerald-500/20 border-emerald-200 text-emerald-700 dark:text-emerald-300">
                    {(llmSuggestion.citations || []).length} cite{(llmSuggestion.citations || []).length === 1 ? "" : "s"}
                  </Badge>
                )}
              </div>

              {/* KB Citations */}
              {(llmSuggestion?.citations || []).length > 0 && (
                <div className="mt-3 space-y-1.5 min-w-0" data-testid="copilot-citations">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Sources</div>
                  {llmSuggestion.citations.map((c, i) => (
                    <a
                      key={c.doc_id + i}
                      href={c.source_url || "#"}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={(e) => { if (!c.source_url) e.preventDefault(); }}
                      className="block rounded-md border border-border bg-card/50 px-2.5 py-1.5 hover:bg-card transition-colors min-w-0"
                      data-testid={`copilot-citation-${i}`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[10px] font-bold text-accent shrink-0">[doc{i + 1}]</span>
                        <span className="text-xs font-medium truncate flex-1 min-w-0">{c.title}</span>
                        {c.source_url && <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0"/>}
                        <span className="text-[10px] text-muted-foreground shrink-0">{(c.score * 100).toFixed(0)}%</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug break-words">{c.snippet}</p>
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2">
                {isDigitalChat && chatChannel !== "email" && !ctx?.read_only ? (
                  <>
                    <Button
                      data-testid="copilot-send-reply"
                      onClick={sendReply}
                      disabled={!suggestionText}
                      className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2"
                    >
                      <Send className="w-4 h-4" /> Send via {chatChannel}
                    </Button>
                    <Button
                      data-testid="copilot-insert-reply"
                      onClick={insertReply}
                      disabled={!suggestionText}
                      variant="outline"
                      className="gap-2"
                      title="Insert into the composer to edit before sending"
                    >
                      <PenLine className="w-4 h-4" /> Insert
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="copilot-send-reply"
                    onClick={sendReply}
                    disabled={!suggestionText}
                    className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-2"
                  >
                    {isDigitalChat
                      ? <><PenLine className="w-4 h-4" /> Insert into reply</>
                      : <><Send className="w-4 h-4" /> Copy Reply</>}
                  </Button>
                )}
              </div>
            </div>

            {/* Objection */}
            {data?.objection && (
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold">Objection Handling</span>
                </div>
                <div className="text-sm">
                  <div className="text-muted-foreground">Objection</div>
                  <div className="font-medium">"{data.objection.objection}"</div>
                  <div className="text-muted-foreground mt-3">Next Action</div>
                  <div className="font-medium">{data.objection.next_action}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
